import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KnowYourCustomerModal } from "@features/KnowYourCustomerModal/KnowYourCustomerModal";
import { useKycFormOpen } from "@features/KnowYourCustomerNotification/utils";
import { Button } from "@components/Button";
import { DocumentViewer } from "@components/DocumentViewer/DocumentViewer";
import { parseHtml } from "@components/HTMLParser/utils";
import { Notification } from "@components/Notification/Notification";
import { useKnowYourCustomerStore } from "@store/knowYourCustomer/knowYourCustomer";
import { useModalActions } from "@store/modal/modal";
import {
  useKnowYourCustomerOverviewQuery,
  useKycFileQuery,
} from "@services/knowYourCustomer/queries";
import { KycState } from "@services/knowYourCustomer/types";

export const KnowYourCustomerNotification: React.FC = () => {
  const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);
  const [showFile, setShowFile] = useState(false);
  const { t } = useTranslation();
  const { verificationStatus } = useKnowYourCustomerStore();
  const { create, remove } = useModalActions();
  const { isPrefetching: isKycFormPrefetching, open: openKycForm } =
    useKycFormOpen();
  const { data: knowYourCustomerOverview } = useKnowYourCustomerOverviewQuery();
  const {
    data: file,
    isFetching: isKycFileFetching,
    refetch: fetchKnowYourCustomerFile,
  } = useKycFileQuery();

  const isLoading = isKycFormPrefetching || isKycFileFetching;

  useEffect(() => {
    if (verificationStatus) {
      const id = create((modalProps) => (
        <KnowYourCustomerModal {...modalProps} />
      ));

      return () => remove(id);
    }
  }, [create, remove, verificationStatus]);

  useEffect(() => {
    setIsSurveyCompleted(
      knowYourCustomerOverview?.knowYourCustomerQuestionnaireInformation
        ?.state === KycState.COMPLETED ||
        knowYourCustomerOverview?.knowYourCustomerQuestionnaireInformation
          ?.state === KycState.COMPLETED_PAYMENT_RESTRICTED,
    );
  }, [knowYourCustomerOverview]);

  const handleButtonClick = async () => {
    if (isSurveyCompleted) {
      await fetchKnowYourCustomerFile()
        .then(() => setShowFile(true))
        .catch(() => toast.error(t("common.unknownErrorOccurred")));
    } else {
      await openKycForm();
    }
  };

  if (
    !knowYourCustomerOverview?.hasQuestionnaire ||
    !knowYourCustomerOverview?.knowYourCustomerQuestionnaireInformation?.state
  ) {
    return null;
  }

  return (
    <>
      <Notification
        color="purple"
        variant={isSurveyCompleted ? "small" : "big"}
        title={t(
          `KYC.notification.${knowYourCustomerOverview.knowYourCustomerQuestionnaireInformation.state}.title`,
        )}
        description={
          parseHtml(
            t(
              `KYC.notification.${knowYourCustomerOverview.knowYourCustomerQuestionnaireInformation.state}.description`,
              {
                fillBefore:
                  knowYourCustomerOverview
                    ?.knowYourCustomerQuestionnaireInformation?.fillBefore,
              },
            ),
          ) as string
        }
        button={
          <Button
            onClick={handleButtonClick}
            variant={isSurveyCompleted ? "link" : "primary"}
            isLoading={isLoading}
            aria-label={t(
              `KYC.notification.${knowYourCustomerOverview.knowYourCustomerQuestionnaireInformation.state}.button`,
            )}
          >
            {t(
              `KYC.notification.${knowYourCustomerOverview.knowYourCustomerQuestionnaireInformation.state}.button`,
            )}
          </Button>
        }
      />
      <DocumentViewer
        file={showFile ? file : undefined}
        onDocumentClose={() => setShowFile(false)}
        isLoading={isKycFileFetching}
        isPdf
      />
    </>
  );
};
